import { differenceInDays, format } from 'date-fns';
import isNil from 'lodash/isNil.js';
import toNumber from 'lodash/toNumber.js';
import CURRENCIES from 'constants/currencies.js';
import { formatInTimeZone } from 'date-fns-tz';

export const numberToText = (num, separator = ',') => {
  return num
    .toString()
    .split('')
    .reverse()
    .map((v, i) => (i % 3 === 0 && i !== 0 ? `${v}${separator}` : v))
    .reverse()
    .join('');
};

/**
 * Converts a decimal number to percent with a given length of decimals
 * @param {number} n
 * @param {number} decimals
 * @return {string} decimal number as a percentage string
 */
export const formatPercentage = (n, decimals = 0) => {
  return `${(n * 100).toFixed(decimals)}%`;
};

/**
 * Calculate a percent
 * @property {number} a
 * @property {number} b
 * @return {number} 0-100
 */
export const calcPercent = (a, b) => {
  if (a && !b) return 100;
  if (!a && b) return 0;
  if (!a && !b) return 0;
  return Math.round((a / b) * 100);
};

/**
 * Abbreviates numbers, e.g;
 * 1200 --> 1.2K
 * 3500000 --> 3.5M
 * @param {number} n
 * @return {string} abbreviated or original number
 */
export const abbreviateNumber = (n) => {
  const abbreviations = ['', 'K', 'M', 'B', 'T'];
  const isNegative = n < 0;
  const absoluteValue = Math.abs(n);
  // Handle numbers less than 1 separately
  if (!absoluteValue) {
    return '0';
  } else if (absoluteValue < 1 && !Number.isInteger(absoluteValue)) {
    return `${isNegative ? '-' : ''}${parseFloat(absoluteValue).toFixed(2)}`;
  }
  const exp = Math.floor(Math.log10(absoluteValue) / 3);
  if (exp >= 0) {
    const abbreviation = Math.min(exp, abbreviations.length - 1);
    let abbreviatedValue = absoluteValue / Math.pow(1000, abbreviation);
    if (!Number.isInteger(abbreviatedValue)) abbreviatedValue = parseFloat(abbreviatedValue.toFixed(exp > 0 ? 1 : 0));
    return `${isNegative ? '-' : ''}${abbreviatedValue}${abbreviations[abbreviation]}`;
  }
  return String(n);
};

/**
 * Converts an input value to a finite number, or returns null if the input is empty,
 * null, undefined, or not a finite number.
 *
 * @param {string|number|null|undefined} value - The value to normalize.
 * @returns {number|null} The finite number representation of the input, or null if invalid.
 */
export const normalizeNumberInput = (value) => {
  if (value === '' || isNil(value)) return null;

  const number = toNumber(value);
  return Number.isFinite(number) ? number : null;
};

/**
 * Format amount using given currency
 * E.g. 100 SEK
 * @param {number} amount
 * @param {string} currency
 * @param {boolean} [abbreviate]
 * @param {number} [precision]
 * @return {string} formatted amount
 */
export const formatAmount = ({ amount, currency, abbreviate = false, precision: overriddenPrecision }) => {
  const amountAsFloat = parseFloat(amount);
  const currencyMeta = CURRENCIES[currency];
  const symbol = currencyMeta ? currencyMeta.symbol : '';
  const code = currencyMeta ? currencyMeta.code : currency;
  const prependSymbol = symbol.length === 1 ? `${symbol} ` : '';
  const appendCode = prependSymbol.length === 0 ? ` ${code}` : '';
  let formattedAmount;

  if (abbreviate && amountAsFloat >= 1000) {
    formattedAmount = abbreviateNumber(amountAsFloat);
  } else {
    const configOrFallbackPrecision =
      currencyMeta && typeof currencyMeta.decimal_digits !== 'undefined' ? currencyMeta.decimal_digits : 2;

    const precision = Number.isInteger(overriddenPrecision) ? overriddenPrecision : configOrFallbackPrecision;
    formattedAmount = numberToText(amountAsFloat.toFixed(precision), ' ');
  }

  return `${prependSymbol}${formattedAmount}${appendCode}`;
};

export const validateEmail = (email) => {
  const emailRegex = /^\S+@\S+\.\S+$/;

  if (!email.match(emailRegex)) {
    return false;
  }
  return true;
};

export const findUniqueValuesByKeys = (obj, props) => {
  const keyFound = (keys, key) => {
    if (Array.isArray(keys)) return props.some(prop => prop === key);
    return keys === key;
  };
  return (res => (JSON.stringify(obj, (key, value) =>
    (keyFound(props, key) && !res.some(v => v === value ) && res.push(value), value)), res))([]);
};

export const flattenObject = (ob) => {
  const toReturn = {};

  for (const i in ob) {
    if (!ob.hasOwnProperty(i)) continue;

    if (typeof ob[i] == 'object' && ob[i] !== null) {
      const flatObject = flattenObject(ob[i]);
      for (const x in flatObject) {
        if (!flatObject.hasOwnProperty(x)) continue;

        toReturn[`${i}.${x}`] = flatObject[x];
      }
    } else {
      toReturn[i] = ob[i];
    }
  }
  return toReturn;
};

export const unflattenObject = (data) => {
  const result = {};
  for (const i in data) {
    const keys = i.split('.');
    keys.reduce((r, e, j) => {
      if (r.hasOwnProperty(e)) {
        return r[e];
      }
      const result = isNaN(Number(keys[j + 1])) ? (keys.length - 1 == j ? data[i] : {}) : [];
      r[e] = result;
      return result;
    }, result);
  }
  return result;
};

/**
 * Get the user's timezone from the browser
 * @returns {string} The user's timezone (e.g., 'America/New_York', 'Europe/London')
 */
export const getUserTimezone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch (error) {
    // Fallback to Europe/Stockholm if detection fails
    console.warn('Could not detect user timezone, falling back to Europe/Stockholm');
    return 'Europe/Stockholm';
  }
};
/**
 * Format timestamp to Date type
 * @param {object} date
 * @return {string} formatted Date() as WEEKDAY MM DD YYYY HH:MM:SS GMT+HHMM
 *
 */
export const formatToDateType = (date) => (date?.seconds ? new Date(date.seconds * 1000) : date);

/**
 * Check if date has passed today's date
 * @param {object} date
 * @return {boolean} Returns true or false
 *
 */
export const hasDatePassed = (date) => formatToDateType(date) < new Date();

/**
 * Gets a duration in days in a more readable format.
 * {to} param defaults to todays date
 * @param {object} date
 * @return {number} Return number of days between two dates
 *
 */
export const durationInDays = (from, to = new Date()) => differenceInDays(formatToDateType(from), to) || 0;

/**
 * Gets browsers locale
 * e.g 'en-US' or 'sv-SE'
 * @return {string} culture code
 *
 */
export const getBrowserLocale = () => window.navigator.userLanguage || window.navigator.language;

const getDateFormattingString = (date) => {
  const currentYear = new Date().getFullYear();
  const dateYear = new Date(date)?.getFullYear?.();
  return dateYear === currentYear ? 'MMM d' : 'MMM d, yyyy';
};

/**
 * Format into translated locale date.
 * Currently support same locales as dashboard translations
 * @return {string} MMM d, yyyy
 *
 */
export const localeDate = (date, strFormat, dateFNSlocale) => {
  const dateFormat = formatToDateType(date);
  const userTimezone = getUserTimezone();
  const formattingString = strFormat || getDateFormattingString(date);
  return formatInTimeZone(dateFormat, userTimezone, formattingString, {
    ...(dateFNSlocale && { locale: dateFNSlocale }),
  });
};

export const localeDateTime = (date, dateFNSlocale) => {
  if (!date) return '';
  const currentYear = new Date().getFullYear();
  const dateYear = new Date(date)?.getFullYear?.();

  const formatString = dateYear === currentYear ? 'MMM d HH:mm' : 'MMM d, yyyy HH:mm';
  return localeDate(date, formatString, dateFNSlocale);
};

export const localeTime = (date, dateFNSlocale) => localeDate(date, 'HH:mm', dateFNSlocale);

/**
 * @param  {Number} {seconds} Timestamp
 * @return {String} Timestamp as a date string
 */
export const formatTimestamp = ({ seconds }) => {
  return new Date(seconds * 1000).toDateString();
};

/**
 * Get if passed variable is an object
 * @param {any} variable
 * @return {boolean} is object?
 */
export const isObject = (variable) => {
  return Object.prototype.toString.call(variable) === '[object Object]';
};

/**
 * Turn value into a slugified string
 * @param  {String} value string to slugify
 * @return {String} slugified string
 */
export const slugify = (value) => {
  return value
    .toString()
    .toLowerCase()
    .normalize('NFD')
    .trim()
    .replace(/\s+/g, '-')
    .replace(/[^\w\-]+/g, '')
    .replace(/\-\-+/g, '-');
};

/**
 * Replace HTML with empty strings to prevent HTML injection.
 * @param {string} - string to replace
 * @returns {string} - replaced string
 */
export const replaceRestrictedCharacters = (string) => {
  const reg = /[!$%^&*()_+|~=`{}\[\]:";<>?\/@]/gi;
  return string.replace(reg, '');
};

/**
 * Determine if input is not undefined or null
 * @param {string} input
 * @return {boolean}
 */
export const hasValue = (input) => input !== null && input !== undefined;

/**
 * A callback function to be called in the catch block of an API call.
 * @callback catchCallback
 * @function
 * @param {Object} error - The error object returned by the API call.
 * @returns {void}
 */

/**
 * A callback function to be called in the finally block of an API call.
 * @callback finallyCallback
 * @function
 * @returns {void}
 */
/**
 * Returns the response of an api call or the error object if the calls fails
 * @param {object} agent - The agent object used to make the API call
 * @param {object} options - The options for the API call, including the URL, method, body, params, and header
 * @param {string} options.url - The URL for the API call
 * @param {string} [options.method='get'] - The HTTP method for the API call (default is 'get')
 * @param {object} [options.body={}] - The request body for the API call (default is an empty object)
 * @param {object} [options.params={}] - The query parameters for the API call (default is an empty object)
 * @param {object} [options.header={}] - The headers for the API call (default is an empty object)
 * @param {object} [functions={}] - Optional functions to be called in the try-catch-finally block
 * @param {catchCallback} [functions.catchCallback] - A callback function to be called in the catch block
 * @param {finallyCallback} [functions.finallyCallback] - A callback function to be called in the finally block
 * @returns {object} The response of the API call or an error object if the call fails
 */
export const handleCall = async (agent, { url, method = 'get', body = {}, params = {}, header = {} }, functions = {}) => {
  const { catchCallback, finallyCallback } = functions;
  try {
    const apiCall = agent[method](url);
    if (method === 'post' || method === 'put' || (method === 'patch' && Object.keys(body).length > 0)) {
      apiCall.send(body);
    }

    if (Object.keys(params).length > 0) {
      const queryString = new URLSearchParams(params).toString();
      apiCall.query(queryString);
    }

    return await apiCall;
  } catch (error) {
    if (process.env.NODE_ENV !== 'test') {
      console.error('error', error);
    }
    catchCallback?.(error);
    const { message, status, response } = error;
    return { error: message, status, response };
  } finally {
    finallyCallback?.();
  }
};

export const formatCompactNumber = (number) => {
  return Intl.NumberFormat('en', { notation: 'compact' }).format(number);
};

/**
 * Checks if provided text is a URL
 * @param {string} text
 * @return {boolean} true if provided text is a URL
 */
export const isURL = (text) => {
  try {
    const url = new URL(text);
    return Boolean(url);
  } catch (e) {
    return false;
  }
};

export const appendQueryParamsToUrl = (url, params) => {
  if (!isURL(url)) return url;

  const urlObj = new URL(url);
  const searchParams = new URLSearchParams(urlObj.search);
  Object.entries(params).forEach(([key, value]) => searchParams.set(key, value));
  urlObj.search = searchParams.toString();
  return urlObj.toString();
};

/**
 * Converts seconds to HH:MM:SS string format
 * @param {number} seconds
 * @return {string} Seconds converted to format in HH:MM:SS
 */
export const convertSecondsToHMS = (seconds) => {
  if (!seconds || seconds <= 0 || isNaN(seconds)) return '00:00:00';

  let hours = Math.floor(seconds / 3600);
  let minutes = Math.floor((seconds % 3600) / 60);
  let remainingSeconds = seconds % 60;

  // Add leading zeros if necessary
  hours = hours.toString().padStart(2, '0');
  minutes = minutes.toString().padStart(2, '0');
  remainingSeconds = remainingSeconds.toString().padStart(2, '0');

  return `${hours}:${minutes}:${remainingSeconds}`;
};

/**
 * Converts a Firestore timestamp to a JavaScript Date object.
 *
 * Firestore timestamps consist of seconds and nanoseconds since the Unix epoch.
 * This function converts these components into a standard JavaScript Date object,
 * which is based on milliseconds since the Unix epoch.
 *
 * @param {Object} timestamp - The Firestore timestamp to convert.
 * @param {number} timestamp.seconds - The number of seconds since the Unix epoch.
 * @param {number} timestamp.nanoseconds - The number of nanoseconds after the seconds.
 * @returns {Date} A JavaScript Date object representing the same point in time.
 */
export const firestoreTimestampToDate = ({ seconds, nanoseconds } = {}) => {
  if (typeof seconds !== 'number' || typeof nanoseconds !== 'number') return null;

  const milliseconds = seconds * 1000 + nanoseconds / 1000000;
  return new Date(milliseconds);
};

/**
 * Returns a sorted array of timezones with their UTC offsets.
 * Each item has a label (e.g. "Europe/Stockholm (UTC +01:00)") and value (IANA timezone name).
 * Sorted by UTC offset from lowest to highest.
 *
 * @returns {Array<{label: string, value: string}>} Array of timezone objects
 */
export const getTimezones = () => {
  if (typeof Intl === 'undefined' || !Intl.supportedValuesOf) return [];

  const now = new Date();
  return Intl.supportedValuesOf('timeZone')
    .map((tz) => {
      const offset = formatInTimeZone(now, tz, 'xxx');
      const label = `${tz.replace(/_/g, ' ')} (UTC ${offset})`;
      return { label, value: tz, offset };
    })
    .sort((a, b) => {
      // Convert "+01:00" to minutes for comparison
      const toMinutes = (offset) => {
        const match = offset.match(/([+-])(\d{2}):(\d{2})/);
        if (!match) return 0;
        const sign = match[1] === '-' ? -1 : 1;
        const hours = parseInt(match[2], 10);
        const minutes = parseInt(match[3], 10);
        return sign * (hours * 60 + minutes);
      };
      return toMinutes(a.offset) - toMinutes(b.offset);
    })
    .map(({ offset, ...rest }) => rest);
};

/**
 * Generates a list of times for each hour (00:00, 01:00, ..., 23:00) in the specified locale.
 * Each item has a value in 'HH:00' 24-hour format and a label as the localized time string.
 *
 * @param {string[]|undefined} [locales] - Optional array of locale strings (defaults to browser locales)
 * @returns {Array<{value: string, label: string}>} Array of time objects for each hour
 */
export const getHourlyTimesForLocale = (locales = window.navigator.languages) => {
  return Array.from({ length: 24 }, (_, hour) => {
    const value = `${hour.toString().padStart(2, '0')}:00`;
    const d = new Date();
    d.setHours(hour, 0, 0, 0);
    const label = d.toLocaleTimeString(locales, { hour: '2-digit', minute: '2-digit' });
    return { value, label };
  });
};
